/* eslint-disable class-methods-use-this,max-classes-per-file */

'use strict';

define('vb/private/events/transformEventBehavior',[
  'vb/private/events/eventBehavior',
  'vb/private/constants',
  'vb/private/log',
  'vb/action/action',
], (EventBehavior, Constants, Log, Action) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   */

  const logger = Log.getLogger('/vb/private/events/transformEventBehavior');

  /**
   * TransformEventBehavior, behavior = "transform"
   */
  class TransformEventBehavior extends EventBehavior {
    /**
     * Executes the transform event behavior.
     *
     * This method triggers the declared event and executes the provided function wrappers in sequence.
     * It logs information about the triggered event and returns a promise that resolves with the result of the
     * execution.
     *
     * @param {Array<{ container: Container, fnc: function }>} functionWrappers - An array of objects where each object
     * contains a container and a function representing the event listener chain. The `fnc` function will be called
     * with the current instance of `EventBehavior`.
     * @returns {Promise<Object>} A promise that resolves with an object containing the result of the execution.
     * @public
     * @overrides
     */
    execute(functionWrappers) {
      logger.info('Triggering declared event, name:', this.name, 'behavior:',
        this.behavior, 'payloadType:', this.payloadType);

      return this.executeInternal(functionWrappers)
        .then((result) => ({ [EventBehavior.ResultProperties.RETURN]: result }));
    }

    /**
     * Calls the (curried) invokeEvent functions serially, and also calls the chains called within
     * each invokeEvent serially.
     *
     * Before calling each chain, `$previous` is added to the expression contexts, to be optionally passed to
     * the chain, as defined by the declared listener.
     *
     * When assigning `'previous'` from the result of an invokeEvent, we need to ignore any that return
     * the `'no listener'` marker.
     *
     * The result is a Promise, resolved with a value that is coerced to the `resultType`.
     *
     * @param {Array<{ container: Container, fnc: function }>} functionWrappers - An array of objects where each object
     * contains a container and a function representing the event listener chain. The `fnc` function will be called
     * with the current instance of `EventBehavior`.
     * @return {Promise<*>} A promise that resolves with the result of the execution, coerced to the `resultType`.
     * @protected
     * @overrides
     */
    executeInternal(functionWrappers) {
      // an initial, valid, empty result to start with
      let previousValidResult = {
        name: Action.Outcomes.SUCCESS,
        result: undefined,
      };

      return functionWrappers
        .reduce((p, wrapper) => Promise.resolve(p)
          .then((result) => {
            previousValidResult = TransformEventBehavior
              .resultToPrevious(result, this.returnType, previousValidResult);
            return wrapper.fnc(this, previousValidResult);
          }),
        Promise.resolve())
        // handle the result of the reduce()
        .then((finalResult) => TransformEventBehavior
          .resultToPrevious(finalResult, this.returnType, previousValidResult));
    }

    /**
     * Calls the chain functions sequentially, passing the expression context and previous result.
     *
     * @param {Container} container - The container holding the context and chaining functions.
     * @param {Array<{chainId: String, fnc: function}>} chainFunctionWrappers - Array of objects where each object
     * contains a chain ID and a function representing the event listener chain.
     * @param {Object} expressionContexts - The contexts to be used while evaluating expressions within the event
     * listener chains.
     * @param {*} previousResult - The previous result to be passed to the functions.
     * returns {Promise<Array<{chainId: String, result: *}>>} - A Promise that resolves to an array of object
     * with the final chain function's name and result.
     * @protected
     * @overrides
     */
    // eslint-disable-next-line no-unused-vars
    callChainFunctionsInternal(container, chainFunctionWrappers, expressionContexts, previousResult) {
      const contexts = expressionContexts;
      contexts[Constants.ContextName.PREVIOUS] = previousResult;

      // as if we came from an action chain for the initial iteration through reduce()
      const initialResult = {
        name: Action.Outcomes.SUCCESS,
        result: expressionContexts[Constants.ContextName.PREVIOUS],
      };

      return chainFunctionWrappers
        .reduce((p, wrapper) => Promise.resolve(p)
          .then((result) => {
            contexts[Constants.ContextName.PREVIOUS] = TransformEventBehavior
              .resultToPrevious(result, this.returnType);
            return wrapper.fnc(contexts);
          }),
        Promise.resolve(initialResult));
    }

    /**
     * Converts the result of a listener invocation (a chain) to the value used for $previous.
     *
     * If the result is not successful, $previous will be set to undefined.
     *
     * @param {Object} result - The result of the listener invocation.
     * @param {String} type - The expected type of the result.
     * @param {*} [lastPrevious] - The last valid result, which is used when the current result is a
     * 'no listener' marker.
     * @returns {*} The converted result, or undefined if the result is not successful.
     */
    static resultToPrevious(result, type, lastPrevious) {
      if (result) {
        if (result.name === Action.Outcomes.SUCCESS) {
          return EventBehavior.shape(result.result, type);
        }
        if (result === Constants.NO_EVENT_LISTENER_RESPONSE) {
          return lastPrevious;
        }
      }
      return undefined;
    }
  }

  return TransformEventBehavior;
});

